/**
 * Day maths for the "Where and when" step. Dates are ISO calendar dates
 * (YYYY-MM-DD) handled as UTC days, so no timezone or DST shift can move
 * them.
 *
 * Counting matches ALMA_LEARN_RULES and alma-finalize: every calendar day,
 * inclusive of both ends. Each city has its own first and last day. A day
 * between two cities (a transit day) is in neither, and is not paid for.
 */
import { DATE_ERRORS, fill } from './flowConfig';

const DAY_MS = 86_400_000;

function toUtc(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

export function isIsoDate(v: string | undefined | null): v is string {
  return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);
}

/** Today in the traveler's own timezone. */
export function todayIso(now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function addDays(iso: string, days: number): string {
  return new Date(toUtc(iso) + days * DAY_MS).toISOString().slice(0, 10);
}

/** Calendar days between two dates; positive when `b` is later. */
export function daysBetween(a: string, b: string): number {
  return Math.round((toUtc(b) - toUtc(a)) / DAY_MS);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-10-14" → "Oct 14". */
export function shortDate(iso: string): string {
  const [, m, d] = iso.split('-').map(Number);
  return `${MONTHS[m - 1]} ${d}`;
}

/** "Oct 14 – 20", or "Oct 28 – Nov 3" across months. */
export function dateRange(start: string, end: string): string {
  const [, sm] = start.split('-').map(Number);
  const [, em, ed] = end.split('-').map(Number);
  return sm === em && start.slice(0, 4) === end.slice(0, 4)
    ? `${shortDate(start)} – ${ed}`
    : `${shortDate(start)} – ${shortDate(end)}`;
}

/** A city's stay: the first and the last day the traveler is there. */
export interface CityDates {
  from: string;
  to: string;
}

/**
 * The date rows for an ordered list of cities: two per city, the first day and
 * the last day in it, in visiting order. Dates are kept per city id so they
 * follow their city when the order changes.
 */
export function rangeRows(cityIds: string[], dates: Record<string, Partial<CityDates>>): string[] {
  return cityIds.flatMap((id) => [dates[id]?.from ?? '', dates[id]?.to ?? '']);
}

/**
 * After cities are added, removed or reordered: keep each entered date only
 * while the dates still run in order (a city starts after the previous one
 * ended, and ends on or after its own first day); clear the rest. Removed
 * cities drop their dates. Days between two cities may stay empty: a transit
 * day belongs to neither city.
 */
export function rebuildDates(
  cityIds: string[],
  dates: Record<string, Partial<CityDates>>,
): Record<string, Partial<CityDates>> {
  const next: Record<string, Partial<CityDates>> = {};
  let last: string | null = null;
  for (const id of cityIds) {
    const entry: Partial<CityDates> = {};
    const from = dates[id]?.from;
    if (isIsoDate(from) && (last === null || from > last)) {
      entry.from = from;
      last = from;
    }
    const to = dates[id]?.to;
    if (isIsoDate(to) && (entry.from ? to >= entry.from : last === null || to > last)) {
      entry.to = to;
      last = to;
    }
    if (entry.from || entry.to) next[id] = entry;
  }
  return next;
}

export interface DateCheck {
  /** One message per row (first day then last day, for each city); undefined when fine. */
  errors: (string | undefined)[];
  /** Every row filled and valid. */
  complete: boolean;
}

/**
 * Validates the rows in order. Only a filled date can be wrong, so nothing
 * complains before the traveler has picked it. Rules: dates are after today; a
 * city's first day is after the previous city's last day (a gap is fine, an
 * overlap is not); a city's last day is on or after its first day (one day is a
 * stay).
 */
export function checkRanges(rows: string[], today: string): DateCheck {
  const errors: (string | undefined)[] = [];
  let previous: string | null = null;
  rows.forEach((d, i) => {
    if (!isIsoDate(d)) {
      errors.push(undefined);
      return;
    }
    const isLastDay = i % 2 === 1;
    // A last day may equal its own first day; everything else must be strictly later.
    const sameCityStart = isLastDay && isIsoDate(rows[i - 1]) && previous === rows[i - 1];
    if (d <= today) {
      errors.push(DATE_ERRORS.past);
    } else if (previous !== null && (sameCityStart ? d < previous : d <= previous)) {
      errors.push(fill(isLastDay && sameCityStart ? DATE_ERRORS.lastDayOrder : DATE_ERRORS.firstDayOrder, { date: shortDate(previous) }));
    } else {
      errors.push(undefined);
    }
    previous = d;
  });
  const complete = rows.length >= 2 && rows.every(isIsoDate) && errors.every((e) => !e);
  return { errors, complete };
}

export interface TripLegs {
  legs: { city: string; start_date: string; days: number }[];
  /** First day of the trip. */
  start_date: string;
  /** Last day of the trip. */
  end_date: string;
  /**
   * Calendar days from the first day to the last, inclusive, transit days
   * between cities included. Completion, availability and the emails all count
   * the trip from `start_date` for this many days. What the traveler pays for
   * is the sum of the legs' own days.
   */
  trip_days: number;
}

/** The cities' stays from valid rows: a city runs from its first day to its last, inclusive. */
export function legsFromRanges(cityNames: string[], rows: string[]): TripLegs {
  const legs = cityNames.map((city, i) => {
    const start = rows[i * 2];
    const end = rows[i * 2 + 1];
    return { city, start_date: start, days: daysBetween(start, end) + 1 };
  });
  const start = rows[0];
  const end = rows[rows.length - 1];
  return { legs, start_date: start, end_date: end, trip_days: daysBetween(start, end) + 1 };
}

const plural = (n: number) => (n === 1 ? '1 day' : `${n} days`);

/** "7 days in Lisbon" / "3 days in Lisbon, then 4 in Porto" / "2 days in Lisbon, 2 in Sintra, then 3 in Porto". */
export function daysLine(legs: { city: string; days: number }[]): string {
  if (legs.length === 0) return '';
  const parts = legs.map((l, i) => (i === 0 ? `${plural(l.days)} in ${l.city}` : `${l.days} in ${l.city}`));
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join(', ')}, then ${parts[parts.length - 1]}`;
}
